import React, { useEffect, useState } from "react";
import { Form, Input, Spin, Tooltip } from "antd";
import { LoadingOutlined, SyncOutlined } from "@ant-design/icons";
import { useAuth } from "../../contexts/auth";
import UpdateCaptureKeyModal from "./Modals/UpdateCaptureKeyModal";
import { createNotification } from "../Notification";
import { listSandboxKeys } from "../../services/account/api";
import UpdateWebhookKeyModal from "./Modals/UpdateWebhookKey";
import UpdateRestApiKeyModal from "./Modals/UpdateRestApiKey";
import { useIntl } from "react-intl";
import { updateSandboxPartnerKey } from "../../services/credentials/api";
import { editWorkspace } from "../../services/workspace/api";

function SandboxForm() {
  const intl = useIntl();

  const { workspaceId, workspaces } = useAuth();

  const { RefreshUser, partner, HasPermission } = useAuth();

  const [data, setData] = useState();
  const [isLoading, setLoading] = useState(true);

  const [isUpdateCaptureKeyModalOpen, setIsUpdateCaptureKeyModalOpen] =
    useState(false);

  const [isUpdateWebhookKeyModalOpen, setIsUpdateWebhookKeyModalOpen] =
    useState(false);

  const [isUpdateRestApiKeyModalOpen, setIsUpdateRestApiKeyModalOpen] =
    useState(false);

  useEffect(() => {
    loadSandboxKeys();
  }, []);

  const loadSandboxKeys = async () => {
    await listSandboxKeys(workspaceId)
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        createNotification("error", "Error", error.response.data.message);
      });
  };

  const [updating, setUpdating] = useState({
    sand_webhook_url: null,
  });

  const generateKey = async (type) => {
    await updateSandboxPartnerKey(workspaceId, { type }).then(() => {
      createNotification(
        "success",
        intl.formatMessage({
          id: "page.integration.capture.modal.success.title",
        }),
        intl.formatMessage({
          id: "page.integration.capture.modal.sandbox.success.description",
        }),
      );
      loadSandboxKeys();
      setIsUpdateRestApiKeyModalOpen(false);
      setIsUpdateCaptureKeyModalOpen(false);
      setIsUpdateWebhookKeyModalOpen(false);
    });
  };

  const reloadKey = async (key) => {
    if (key === "capture_key") {
      setIsUpdateCaptureKeyModalOpen(true);
    }
    if (key === "restapi_key") {
      setIsUpdateRestApiKeyModalOpen(true);
    }
    if (key === "webhook_key") {
      setIsUpdateWebhookKeyModalOpen(true);
    }
  };

  const handleBlur = async (e) => {
    let payload = {};
    let temp = updating;
    const field = e.target.id;

    if (updating[field] === true) {
      temp[field] = "validating";
      setUpdating({ ...updating, ...temp });
      payload[field] = e.target.value;
      await editWorkspace(workspaceId, payload)
        .then(() => {
          temp[field] = "success";
          setUpdating({ ...updating, ...temp });
          RefreshUser();
        })
        .catch(() => {
          temp[field] = "error";
          setUpdating({ ...updating, ...temp });
        });
    }
  };

  function getCurrentWorkspace() {
    return workspaces.find((workspace) => workspace.external_id === workspaceId);
  }

  const renderMessage = (status) => {
    if (updating[status] === "validating") return "Data is being saved...";
    if (updating[status] === "error")
      return "Data can't be saved. Try again later...";
    if (updating[status] === "success") return "Data was updated successfully.";
  };

  if (isLoading) {
    return (
      <div className="py-5 px-7 min-h-[418px] rounded-xl flex justify-center items-center bg-white border-1 shadow-sm">
        <Spin size="large" indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  return (
    <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm">
      <Form layout="vertical">
        <Form.Item
          label={intl.formatMessage({ id: "page.integration.capture-key" })}
        >
          <Input
            size="large"
            readOnly
            value={data.capture_key}
            addonAfter={
              HasPermission("refresh_sandbox_credentials") ? (
                <Tooltip title="Refresh">
                  <SyncOutlined onClick={() => reloadKey("capture_key")} />
                </Tooltip>
              ) : null
            }
          />
        </Form.Item>
        <Form.Item
          label="Webhook URL:"
          hasFeedback
          validateStatus={updating.sand_webhook_url}
          help={renderMessage("sand_webhook_url")}
          rules={[
            {
              required: true,
              message: "Please input your sandbox webhook URL!",
            },
          ]}
        >
          <Input
            id="sand_webhook_url"
            size="large"
            addonBefore="https://"
            readOnly={!HasPermission("refresh_sandbox_credentials")}
            defaultValue={getCurrentWorkspace().sand_webhook_url}
            onBlur={(e) => handleBlur(e)}
            onInput={(e) => {
              setUpdating({ ...updating, sand_webhook_url: true });
            }}
            type="text"
            placeholder="Set the webhook URL to receive the transactions."
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ id: "page.integration.webhook-key" })}
        >
          <Input
            size="large"
            readOnly
            value={data.webhook_key}
            addonAfter={
              HasPermission("refresh_sandbox_credentials") ? (
                <Tooltip title="Refresh">
                  <SyncOutlined onClick={() => reloadKey("webhook_key")} />
                </Tooltip>
              ) : null
            }
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ id: "page.integration.restapi-key" })}
        >
          <Input
            size="large"
            readOnly
            value={data.restapi_key}
            addonAfter={
              HasPermission("refresh_sandbox_credentials") ? (
                <Tooltip title="Refresh">
                  <SyncOutlined onClick={() => reloadKey("restapi_key")} />
                </Tooltip>
              ) : null
            }
          />
        </Form.Item>
      </Form>
      <UpdateCaptureKeyModal
        open={isUpdateCaptureKeyModalOpen}
        onCancel={() => setIsUpdateCaptureKeyModalOpen(false)}
        onSubmit={() => {
          generateKey("capture");
        }}
        environment={"sandbox"}
        environment_label={"sandbox"}
      />
      <UpdateWebhookKeyModal
        open={isUpdateWebhookKeyModalOpen}
        onCancel={() => setIsUpdateWebhookKeyModalOpen(false)}
        onSubmit={() => {
          generateKey("webhook");
        }}
        environment={"sandbox"}
        environment_label={"sandbox"}
      />
      <UpdateRestApiKeyModal
        open={isUpdateRestApiKeyModalOpen}
        onCancel={() => setIsUpdateRestApiKeyModalOpen(false)}
        onSubmit={() => {
          generateKey("restapi");
        }}
        environment={"sandbox"}
        environment_label={"sandbox"}
      />
    </div>
  );
}
export default SandboxForm;
